import { useCallback, useEffect, useRef, useState } from "react";
import {
  studioNotifications,
  type NotificationAction,
  type StudioNotification,
} from "@/src/services/studioNotifications";

export function useStudioNotifications() {
  const [items, setItems] = useState<StudioNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const alive = useRef(false);
  const busy = useRef(false);
  const refreshedAt = useRef(0);

  const refresh = useCallback(async (force = false) => {
    if (busy.current || (!force && Date.now() - refreshedAt.current < 30_000))
      return;
    busy.current = true;
    setLoading(true);
    try {
      const data = await studioNotifications.list();
      if (alive.current) {
        setItems(data);
        setError(false);
        refreshedAt.current = Date.now();
      }
    } catch {
      if (alive.current) setError(true);
    } finally {
      busy.current = false;
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void refresh();
    const onFocus = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const onOnline = () => {
      void refresh(true);
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive.current = false;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  const update = useCallback(
    async (ids: string[], action: NotificationAction) => {
      if (busy.current || !ids.length) return false;
      busy.current = true;
      setLoading(true);
      try {
        await studioNotifications.update(ids, action);
        if (!alive.current) return false;
        const field =
          action === "archive" || action === "restore"
            ? "archivedAt"
            : "readAt";
        const value =
          action === "read" || action === "archive"
            ? new Date().toISOString()
            : null;
        setItems((previous) =>
          previous.map((item) =>
            ids.includes(item.id) ? { ...item, [field]: value } : item,
          ),
        );
        setError(false);
        return true;
      } catch {
        if (alive.current) setError(true);
        return false;
      } finally {
        busy.current = false;
        if (alive.current) setLoading(false);
      }
    },
    [],
  );
  return { items, loading, error, refresh, update };
}
